import { types, getSnapshot, applySnapshot } from 'mobx-state-tree';
import creditcardutils from 'creditcardutils';
import { creditCardExpirationPattern } from 'js/services/Regex';
import { validCreditCard, validCreditCardExpiration, validCvc } from 'js/services/Regex';
import { castEventToValue } from 'js/services/FormTools';
import { blueColor } from 'js/config/style';
import Address from './Address';

export function formatCCNumber(cc_number, cc_type = null) {
  cc_type = cc_type || creditcardutils.parseCardType(cc_number);
  return cc_type === 'amex'
    ? `${cc_number.substr(0, 4)} ${cc_number.substr(4, 6)} ${cc_number.substr(10)}`.trim()
    : `${(cc_number || ' ')
        .match(/.{1,4}/g)
        .join(' ')
        .trim()}`;
}

export function checkIfCCNumberIsValid(e){
  //if the input is empty reset the indicators to their default classes
  if (e.target.value == ''){
    if(e.target.previousElementSibling && e.target.previousElementSibling.className){
      e.target.previousElementSibling.className = 'card-type';
    }
    if(e.target.nextElementSibling && !e.target.nextElementSibling.firstElementChild){
      return;
    }
    e.target.nextElementSibling.firstElementChild.className = 'card-valid';
    return
  }

  //Retrieve the value of the input and remove all non-number characters
  var number = String(e.target.value);
  var cleanNumber = '';
  for (var i = 0; i<number.length; i++){
    if (/^[0-9]+$/.test(number.charAt(i))){
      cleanNumber += number.charAt(i);
    }
  }

  //Only parse and correct the input value if the key pressed isn't backspace.
  if (e.key != 'Backspace'){
    //Format the value to include spaces in the correct locations
    var formatNumber = '';
    for (var i = 0; i<cleanNumber.length; i++){
      if (i == 3 || i == 7 || i == 11 ){
        formatNumber = formatNumber + cleanNumber.charAt(i) + ' '
      }else{
        formatNumber += cleanNumber.charAt(i)
      }
    }
    e.target.value = formatNumber;
  }

  //run the Luhn algorithm on the number if it is at least equal to the shortest card length
  if (cleanNumber.length >= 12){
    var isLuhn = luhn(cleanNumber);
  }

  function luhn(number){
    var numberArray = number.split('').reverse();
    for (var i=0; i<numberArray.length; i++){
      if (i%2 != 0){
        numberArray[i] = numberArray[i] * 2;
        if (numberArray[i] > 9){
          numberArray[i] = parseInt(String(numberArray[i]).charAt(0)) + parseInt(String(numberArray[i]).charAt(1))
        }
      }
    }
    var sum = 0;
    for (var i=1; i<numberArray.length; i++){
      sum += parseInt(numberArray[i]);
    }
    sum = sum * 9 % 10;
    return numberArray[0] == sum;
  }

  //if the number passes the Luhn algorithm add the class 'active'
  if(e.target.nextElementSibling.firstElementChild){
    if (isLuhn == true){
      e.target.nextElementSibling.firstElementChild.className = 'card-valid active'
    }else{
      e.target.nextElementSibling.firstElementChild.className = 'card-valid'
    }
  }

  var card_types = [
    {
      name: 'amex',
      pattern: /^3[47]/,
      valid_length: [15]
    }, {
      name: 'diners_club_carte_blanche',
      pattern: /^30[0-5]/,
      valid_length: [14]
    }, {
      name: 'diners_club_international',
      pattern: /^36/,
      valid_length: [14]
    }, {
      name: 'jcb',
      pattern: /^35(2[89]|[3-8][0-9])/,
      valid_length: [16]
    }, {
      name: 'laser',
      pattern: /^(6304|670[69]|6771)/,
      valid_length: [16, 17, 18, 19]
    }, {
      name: 'visa_electron',
      pattern: /^(4026|417500|4508|4844|491(3|7))/,
      valid_length: [16]
    }, {
      name: 'visa',
      pattern: /^4/,
      valid_length: [16]
    }, {
      name: 'mastercard',
      pattern: /^5[1-5]/,
      valid_length: [16]
    }, {
      name: 'maestro',
      pattern: /^(5018|5020|5038|6304|6759|676[1-3])/,
      valid_length: [12, 13, 14, 15, 16, 17, 18, 19]
    }, {
      name: 'discover',
      pattern: /^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)/,
      valid_length: [16]
    }
  ];

  //test the number against each of the above card types and regular expressions
  for (var i = 0; i< card_types.length; i++){
    if (number.match(card_types[i].pattern) && e.target.previousElementSibling){
      //if a match is found add the card type as a class
      e.target.previousElementSibling.className = 'card-type '+card_types[i].name;
    }
  }
}

export default types
  .model('CreditCard', {
    id: 0,
    name: '',
    number: '',
    expire: '',
    cvc: '',
    address: types.optional(Address, {}),
    type: '',
    is_verified: false,
  })
  .views(self => ({
    get isNew() {
      return !self.id;
    },
    get hasAddressError() {
      // console.log('hasAddressError', !self.address.formatted_address.length, '111', self.address.hasAddress);
      return !self.address.hasAddress;
    },
    get cc_type() {
      if (self.number && (self.number.replace(/\D/g, '').length > 4 || !self.type)) {
        return !self.number.match(/^#/) ? creditcardutils.parseCardType(self.number) : null;
      } else {
        return self.type;
      }
    },
    get isFilledIn() {
      return !!(
        this.name.length &&
        validCreditCard(this.number) &&
        validCreditCardExpiration(this.expire) &&
        (validCvc(this) || this.id) &&
        (this.address && this.address.formatted_address)
      );
    },
    get institution_color() {
      return blueColor;
    },
  }))
  .actions(self => ({
    setId(id) {
      self.id = castEventToValue(id);
      return self;
    },
    setName(name) {
      self.name = castEventToValue(name);
      return self;
    },
    rawSetNumber(number) {
      self.number = castEventToValue(number);
      return self;
    },
    setNumber(number) {
      self.number = formatCCNumber(castEventToValue(number).replace(/([\D])*/g, ''));
      return self;
    },
    checkIfCCNumberIsValid(number){
      checkIfCCNumberIsValid(number);
      return self;
    },
    setExpire(expire) {
      expire = castEventToValue(expire);
      const matches = expire.match(creditCardExpirationPattern);
      self.expire = matches ? matches.splice(1).join('/') : expire;
      return self;
    },
    setCvc(cvc) {
      self.cvc = castEventToValue(cvc);
      return self;
    },
    setBillingAddress(billingAddress) {
      self.address = { ...billingAddress};
      return self;
    },
    setAddress(billingAddress) {
      self.address = { ...billingAddress };
      return self;
    },
    addressSaved(savedAddress) {
      // console.log('addressSaved', savedAddress);
      // if(self.billing_address && savedAddress){
        self.address.setAddress({ ...savedAddress});
      // }
      // self.modalIsVisible = false;
      return self;
    },
    setCreditCard(snapshot) {
      let obj = { ...getSnapshot(self), ...snapshot };

      if(obj.address){
          obj.address = { ...getSnapshot(obj.address), ...snapshot.address };
      }

      applySnapshot(self, snapshot ? obj : {});
      return self;
      // applySnapshot(self, snapshot ? { ...getSnapshot(self), ...snapshot } : {});
      // return self;
    },
  }));
